/**
 * Creative — per-client asset workspace.
 *
 *  1. Upload source files (any type — images, audio, video, PDFs, text).
 *  2. Add hosted-video URLs (YouTube / Vimeo — thumbnail + metadata pulled
 *     automatically).
 *  3. Create with AI — pick a KIND (Image / Text / Audio / Video), a
 *     provider inside that kind, review the cost, generate, and save
 *     to the Library with the correct licence + provider metadata.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Upload, Sparkles, Loader2, Coins, X, Check, Wand2, Image as ImageIcon,
  FileAudio, FileVideo, FileText, Type, Youtube, Link as LinkIcon,
} from "lucide-react";
import { toast } from "sonner";

const ASPECTS = [
  { value: "1:1", label: "Square (1:1)" },
  { value: "4:5", label: "Portrait (4:5)" },
  { value: "9:16", label: "Story (9:16)" },
  { value: "16:9", label: "Landscape (16:9)" },
];
const fmtCost = (n) => (n == null ? "—" : n < 0.01 ? `$${n.toFixed(4)}` : `$${n.toFixed(3)}`);
export const assetFileUrl = (a) => `${API}/creative/assets/${a.asset_id}/file`;

const KIND_META = {
  image: { label: "Image", icon: ImageIcon },
  text: { label: "Text / Copy", icon: Type },
  audio: { label: "Audio / Voice", icon: FileAudio },
  video: { label: "Video", icon: FileVideo },
};

const MimeIcon = ({ mime }) => {
  if ((mime || "").startsWith("audio/")) return <FileAudio className="h-6 w-6 text-zinc-400" />;
  if ((mime || "").startsWith("video/")) return <FileVideo className="h-6 w-6 text-zinc-400" />;
  return <FileText className="h-6 w-6 text-zinc-400" />;
};

export default function CreativePage() {
  const { clientId } = useParams();

  // Library + providers
  const [assets, setAssets] = useState([]);
  const [providers, setProviders] = useState([]);

  // Uploads
  const [staged, setStaged] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // URL asset (YouTube/Vimeo)
  const [urlDialog, setUrlDialog] = useState(false);
  const [urlValue, setUrlValue] = useState("");
  const [urlName, setUrlName] = useState("");
  const [urlDesc, setUrlDesc] = useState("");
  const [urlBusy, setUrlBusy] = useState(false);

  // AI generation
  const [kind, setKind] = useState("image"); // image | text | audio | video
  const [provider, setProvider] = useState("nano_banana_2_lite");
  const [brief, setBrief] = useState("");
  const [audioText, setAudioText] = useState("");
  const [aspect, setAspect] = useState("1:1");
  const [duration, setDuration] = useState(5);
  const [voice, setVoice] = useState("alloy");
  const [selectedRefs, setSelectedRefs] = useState(new Set());
  const [costPreview, setCostPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState([]);
  const [saveTarget, setSaveTarget] = useState(null); // {job_id, filename, kind}
  const [saveName, setSaveName] = useState("");
  const [saveDesc, setSaveDesc] = useState("");

  const reload = useCallback(() => {
    api.get(`/creative/clients/${clientId}/assets`)
      .then((r) => setAssets(r.data || [])).catch(() => setAssets([]));
  }, [clientId]);
  useEffect(() => {
    reload();
    api.get(`/creative/providers`).then((r) => setProviders(r.data || [])).catch(() => {});
  }, [clientId, reload]);

  const providersByKind = useMemo(() => {
    const g = { image: [], text: [], audio: [], video: [] };
    providers.forEach((p) => { if (g[p.kind]) g[p.kind].push(p); });
    return g;
  }, [providers]);

  // Reset provider when kind changes
  useEffect(() => {
    const first = (providersByKind[kind] || [])[0];
    if (first && !providersByKind[kind]?.some((p) => p.key === provider)) {
      setProvider(first.key);
    }
  }, [kind, providersByKind, provider]);

  const currentProvider = providers.find((p) => p.key === provider);

  // ── Uploads ──────────────────────────────────────────────────
  const stageFiles = (fileList) => {
    setStaged((prev) => [...prev, ...Array.from(fileList).map((f) => ({
      file: f, name: f.name.replace(/\.[^.]+$/, ""), description: "", license: "",
    }))]);
  };
  const setStagedField = (i, k, v) =>
    setStaged((prev) => prev.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));

  const uploadStaged = async () => {
    setUploading(true);
    try {
      for (const s of staged) {
        const fd = new FormData();
        fd.append("file", s.file);
        fd.append("name", s.name);
        fd.append("description", s.description);
        fd.append("license", s.license);
        await api.post(`/creative/clients/${clientId}/assets`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Uploaded ${staged.length} file(s) to the Library`);
      setStaged([]);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  // ── URL asset ─────────────────────────────────────────────────
  const saveUrl = async () => {
    setUrlBusy(true);
    try {
      await api.post(`/creative/clients/${clientId}/url-assets`, {
        url: urlValue.trim(),
        name: urlName.trim() || null,
        description: urlDesc.trim(),
      });
      toast.success("Video URL added to the Library");
      setUrlDialog(false);
      setUrlValue(""); setUrlName(""); setUrlDesc("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add video URL");
    } finally { setUrlBusy(false); }
  };

  // ── AI generation ─────────────────────────────────────────────
  const toggleRef = (assetId) => setSelectedRefs((prev) => {
    const next = new Set(prev);
    if (next.has(assetId)) next.delete(assetId); else next.add(assetId);
    return next;
  });

  const pollJob = async (jobId, onDone) => {
    for (let i = 0; i < 100; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      let j;
      try { ({ data: j } = await api.get(`/creative/jobs/${jobId}`)); }
      catch { continue; }
      if (j.status === "done") { onDone(j); return true; }
      if (j.status === "failed") {
        toast.error(j.error || "Generation failed");
        return true;
      }
    }
    toast.error("Still rendering after 5 minutes — check back on this tab shortly.");
    return false;
  };

  const generate = async () => {
    const estimateBody = {
      provider,
      aspect_ratio: aspect,
      chars: kind === "audio" ? audioText.length : (kind === "text" ? brief.length : undefined),
      duration_seconds: duration,
    };
    let est;
    try {
      ({ data: est } = await api.post(
        `/creative/clients/${clientId}/estimate`, estimateBody));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't estimate cost");
      return;
    }
    setCostPreview({
      data: est,
      onConfirm: async () => {
        setCostPreview(null);
        setGenerating(true);
        try {
          if (kind === "image") {
            const { data: job } = await api.post(
              `/creative/clients/${clientId}/generate`, {
                brief, provider, aspect_ratio: aspect,
                reference_asset_ids: Array.from(selectedRefs),
              });
            await pollJob(job.job_id, (j) => {
              setResults((prev) => [
                ...(j.files || []).map((f) => ({
                  ...f, job_id: job.job_id, kind: "image",
                })), ...prev]);
              toast.success(`Created ${j.files?.length || 0} image(s) · ${fmtCost(j.cost_usd)}`);
            });
          } else if (kind === "text") {
            const { data: job } = await api.post(
              `/creative/clients/${clientId}/generate-text`, {
                brief, providers: [provider],
              });
            await pollJob(job.job_id, (j) => {
              const good = (j.results || []).filter((r) => r.ok);
              const totalCost = good.reduce((s, r) => s + (r.cost_usd || 0), 0);
              setResults((prev) => [
                ...good.map((r) => ({
                  job_id: job.job_id, kind: "text",
                  provider: r.provider, text: r.text, model: r.model,
                })), ...prev]);
              toast.success(`Wrote ${good.length} draft(s) · ${fmtCost(totalCost)}`);
            });
          } else if (kind === "audio") {
            const { data: job } = await api.post(
              `/creative/clients/${clientId}/generate-audio`, {
                text: audioText, provider, voice: voice || undefined,
              });
            await pollJob(job.job_id, (j) => {
              setResults((prev) => [
                ...(j.files || []).map((f) => ({
                  ...f, job_id: job.job_id, kind: "audio",
                })), ...prev]);
              toast.success(`Voice ready · ${fmtCost(j.cost_usd)}`);
            });
          } else if (kind === "video") {
            const { data: job } = await api.post(
              `/creative/clients/${clientId}/generate-video`, {
                brief, provider, aspect_ratio: aspect,
                duration_seconds: duration,
              });
            await pollJob(job.job_id, (j) => {
              if (j.video_url) {
                setResults((prev) => [{
                  job_id: job.job_id, kind: "video",
                  video_url: j.video_url, mime: "video/mp4",
                }, ...prev]);
                toast.success(`Video ready · ${fmtCost(j.cost_usd)}`);
              }
            });
          }
        } catch (e) {
          toast.error(e.response?.data?.detail || "Generation failed");
        } finally { setGenerating(false); }
      },
    });
  };

  // ── Save result to Library ──────────────────────────────────
  const openSave = (r) => {
    setSaveTarget(r);
    setSaveName(
      r.kind === "text"
        ? (brief.slice(0, 60) || "AI copy draft")
        : ((brief || audioText).slice(0, 60) || r.filename || "AI asset"));
    setSaveDesc("");
  };
  const saveToLibrary = async () => {
    try {
      if (saveTarget.kind === "text") {
        await api.post(`/creative/jobs/${saveTarget.job_id}/save-text`, {
          provider: saveTarget.provider,
          name: saveName, description: saveDesc,
        });
      } else if (saveTarget.kind === "video" && !saveTarget.filename) {
        await api.post(`/creative/jobs/${saveTarget.job_id}/save-video-url`, {
          filename: "seedance-video",
          name: saveName, description: saveDesc,
        });
      } else {
        await api.post(`/creative/jobs/${saveTarget.job_id}/save`, {
          filename: saveTarget.filename,
          name: saveName, description: saveDesc,
        });
      }
      setResults((prev) => prev.map((r) =>
        r.job_id === saveTarget.job_id
          && (r.filename === saveTarget.filename
              || r.provider === saveTarget.provider
              || (r.kind === "video" && saveTarget.kind === "video"))
          ? { ...r, saved: true } : r));
      setSaveTarget(null);
      toast.success("Saved to Library");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const imageAssets = assets.filter((a) => (a.mime || "").startsWith("image/"));
  const showAspect = kind === "image" || kind === "video";
  const showDuration = kind === "video";
  const showBrief = kind !== "audio";
  const showAudioText = kind === "audio";
  const showRefs = kind === "image" && !!imageAssets.length;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="creative-page">
      <header>
        <div className="flex items-center gap-2 text-xs label-overline text-[#002FA7]">
          <Wand2 className="h-3 w-3" /> Creative
        </div>
        <p className="text-sm text-zinc-500 mt-1">
          Upload files, add YouTube/Vimeo links, or brief the AI to create
          images, copy, voice or video. Everything lands in the Library.
        </p>
      </header>

      {/* 1 · Upload */}
      <Card className="rounded-sm">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Label className="label-overline">1 · Upload source files</Label>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-sm"
                onClick={() => setUrlDialog(true)}
                data-testid="creative-add-url-btn">
                <Youtube className="h-4 w-4 mr-1.5" /> Add video URL
              </Button>
              <input ref={fileRef} type="file" multiple className="hidden"
                accept="image/*,audio/*,video/*,application/pdf,text/*"
                onChange={(e) => { stageFiles(e.target.files); e.target.value = ""; }} />
              <Button variant="outline" className="rounded-sm"
                onClick={() => fileRef.current?.click()}
                data-testid="creative-pick-files">
                <Upload className="h-4 w-4 mr-1.5" /> Choose files
              </Button>
            </div>
          </div>
          {!!staged.length && (
            <div className="space-y-2" data-testid="creative-staged-list">
              {staged.map((s, i) => (
                <div key={`${s.file.name}-${i}`}
                  className="grid grid-cols-12 gap-2 items-center rounded-sm border border-zinc-200 p-2">
                  <div className="col-span-12 md:col-span-3 text-xs font-mono truncate"
                    title={s.file.name}>{s.file.name}</div>
                  <Input className="col-span-6 md:col-span-2 rounded-sm h-8 text-xs" value={s.name}
                    placeholder="Display name"
                    onChange={(e) => setStagedField(i, "name", e.target.value)}
                    data-testid={`staged-name-${i}`} />
                  <Input className="col-span-6 md:col-span-4 rounded-sm h-8 text-xs" value={s.description}
                    placeholder="Description — what is this file?"
                    onChange={(e) => setStagedField(i, "description", e.target.value)}
                    data-testid={`staged-description-${i}`} />
                  <Input className="col-span-10 md:col-span-2 rounded-sm h-8 text-xs" value={s.license}
                    placeholder="Licence (optional)"
                    onChange={(e) => setStagedField(i, "license", e.target.value)} />
                  <button className="col-span-2 md:col-span-1 text-red-500 hover:text-red-700 grid place-items-center"
                    onClick={() => setStaged((prev) => prev.filter((_, idx) => idx !== i))}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <Button onClick={uploadStaged} disabled={uploading}
                className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="creative-upload-btn">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
                Upload {staged.length} file(s) to Library
              </Button>
            </div>
          )}
          {!staged.length && (
            <p className="text-xs text-zinc-400">
              Images, audio, video, text or PDFs (max 50 MB each). YouTube /
              Vimeo links are pulled by URL — no local file required.
            </p>
          )}
        </CardContent>
      </Card>

      {/* 2 · Create with AI */}
      <Card className="rounded-sm">
        <CardContent className="p-5 space-y-4">
          <Label className="label-overline">2 · Create with AI</Label>

          {/* Kind picker */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(KIND_META).map(([k, meta]) => {
              const Icon = meta.icon;
              const on = kind === k;
              return (
                <button key={k} type="button" onClick={() => setKind(k)}
                  className={`rounded-sm px-3 py-1.5 text-xs font-medium border transition-colors
                    ${on ? "border-[#002FA7] bg-[#002FA7] text-white"
                         : "border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}
                  data-testid={`creative-kind-${k}`}>
                  <Icon className="h-3.5 w-3.5 mr-1 inline" />
                  {meta.label}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2 space-y-3">
              {showBrief && (
                <div>
                  <Label className="text-xs text-zinc-500">
                    {kind === "text" ? "Brief — what should the copy achieve?" : "Brief"}
                  </Label>
                  <Textarea rows={4} value={brief} onChange={(e) => setBrief(e.target.value)}
                    className="rounded-sm mt-1" data-testid="creative-brief"
                    placeholder={
                      kind === "text"
                        ? "e.g. LinkedIn post announcing our regional FM tower launch, punchy and human, 100 words max…"
                        : kind === "video"
                          ? "e.g. cinematic drone shot pulling out from our new FM tower at golden hour, warm palette, subtle wind, no text"
                          : "e.g. hero banner of our new regional FM tower at sunset, warm tones, space for a headline top-left…"
                    } />
                </div>
              )}
              {showAudioText && (
                <div>
                  <Label className="text-xs text-zinc-500">Script — the exact words to speak</Label>
                  <Textarea rows={5} value={audioText}
                    onChange={(e) => setAudioText(e.target.value)}
                    className="rounded-sm mt-1" data-testid="creative-audio-text"
                    placeholder="e.g. Introducing Coastline FM — the sound of the South Coast. Now broadcasting on 104.5 FM…" />
                  <div className="text-[10px] text-zinc-400 mt-1">
                    {audioText.length} characters
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-zinc-500">AI platform</Label>
                <Select value={provider} onValueChange={setProvider}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="creative-provider">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(providersByKind[kind] || []).map((p) => (
                      <SelectItem key={p.key} value={p.key}>
                        {p.label}{p.recommended ? " · recommended" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {currentProvider?.cost_hint && (
                  <div className="text-[10px] text-zinc-400 mt-1">
                    <Coins className="h-3 w-3 inline mr-0.5" />
                    {currentProvider.cost_hint}
                  </div>
                )}
              </div>
              {showAspect && (
                <div>
                  <Label className="text-xs text-zinc-500">Aspect ratio</Label>
                  <Select value={aspect} onValueChange={setAspect}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="creative-aspect">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ASPECTS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {showDuration && (
                <div>
                  <Label className="text-xs text-zinc-500">Duration (seconds)</Label>
                  <Input type="number" min={4} max={15} value={duration}
                    onChange={(e) => setDuration(Math.max(4, Math.min(15, Number(e.target.value) || 5)))}
                    className="rounded-sm mt-1" data-testid="creative-duration" />
                </div>
              )}
              {kind === "audio" && (
                <div>
                  <Label className="text-xs text-zinc-500">
                    {currentProvider?.needs_voice_id ? "ElevenLabs voice ID" : "Voice"}
                  </Label>
                  {currentProvider?.needs_voice_id ? (
                    <Input value={voice} onChange={(e) => setVoice(e.target.value)}
                      placeholder="e.g. 21m00Tcm4TlvDq8ikWAM (Rachel)"
                      className="rounded-sm mt-1 font-mono text-xs"
                      data-testid="creative-voice-id" />
                  ) : (
                    <Select value={voice} onValueChange={setVoice}>
                      <SelectTrigger className="rounded-sm mt-1" data-testid="creative-voice">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(currentProvider?.voices || ["alloy"]).map((v) => (
                          <SelectItem key={v} value={v}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
            </div>
          </div>

          {showRefs && (
            <div className="rounded-sm bg-zinc-50 p-3 space-y-2">
              <div className="text-xs">
                <span className="font-bold">Reference images</span> — tick any Library images the AI should use as visual context.
              </div>
              <div className="flex flex-wrap gap-2">
                {imageAssets.map((a) => {
                  const on = selectedRefs.has(a.asset_id);
                  return (
                    <button key={a.asset_id} type="button" onClick={() => toggleRef(a.asset_id)}
                      className={`relative rounded-sm border overflow-hidden ${on ? "border-[#002FA7] ring-2 ring-[#002FA7]/40" : "border-zinc-200"}`}
                      title={a.name} data-testid={`creative-ref-${a.asset_id}`}>
                      <img src={assetFileUrl(a)} alt={a.name} className="h-16 w-16 object-cover" />
                      {on && (
                        <span className="absolute top-0.5 right-0.5 bg-[#002FA7] text-white rounded-full p-0.5">
                          <Check className="h-3 w-3" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <Button onClick={generate}
            disabled={generating
              || (showBrief && brief.trim().length < 4)
              || (showAudioText && audioText.trim().length < 4)}
            className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
            data-testid="creative-generate">
            {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
            {generating ? "Creating — this can take a minute or two…"
              : `Create ${KIND_META[kind].label.toLowerCase()}`}
          </Button>
        </CardContent>
      </Card>

      {/* 3 · Results */}
      {!!results.length && (
        <Card className="rounded-sm">
          <CardContent className="p-5 space-y-3">
            <Label className="label-overline">3 · Results — save to Library or discard</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="creative-results">
              {results.map((r, i) => (
                <div key={`${r.job_id}-${r.filename || r.provider || i}`}
                  className="rounded-sm border border-zinc-200 overflow-hidden">
                  {r.kind === "image" && (
                    <a href={`${API}/creative/jobs/${r.job_id}/files/${r.filename}`}
                      target="_blank" rel="noreferrer">
                      <img src={`${API}/creative/jobs/${r.job_id}/files/${r.filename}`}
                        alt="" className="w-full h-56 object-cover" />
                    </a>
                  )}
                  {r.kind === "audio" && (
                    <div className="p-3 bg-zinc-50 flex flex-col items-center justify-center gap-2 h-56">
                      <FileAudio className="h-8 w-8 text-zinc-400" />
                      <audio src={`${API}/creative/jobs/${r.job_id}/files/${r.filename}`}
                        controls className="w-full" />
                    </div>
                  )}
                  {r.kind === "video" && r.video_url && (
                    <video src={r.video_url} controls
                      className="w-full h-56 bg-black object-cover" />
                  )}
                  {r.kind === "text" && (
                    <div className="p-3 h-56 overflow-auto bg-zinc-50">
                      <div className="text-[10px] font-mono text-zinc-400 mb-1">
                        {r.provider}{r.model ? ` · ${r.model}` : ""}
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{r.text}</p>
                    </div>
                  )}
                  <div className="p-2 space-y-1.5">
                    {r.saved ? (
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 rounded-sm"
                        variant="outline">
                        <Check className="h-3 w-3 mr-1" /> In Library
                      </Badge>
                    ) : (
                      <div className="flex gap-1.5">
                        <Button size="sm"
                          className="rounded-sm h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                          onClick={() => openSave(r)}
                          data-testid={`creative-save-${i}`}>
                          Save to Library
                        </Button>
                        <Button size="sm" variant="ghost"
                          className="rounded-sm h-7 text-xs text-zinc-500"
                          onClick={() => setResults((prev) => prev.filter((_, idx) => idx !== i))}>
                          Discard
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cost warning */}
      <Dialog open={!!costPreview} onOpenChange={(o) => !o && setCostPreview(null)}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="creative-cost-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="h-4 w-4 text-amber-600" /> Confirm AI creation
            </DialogTitle>
            <DialogDescription>{costPreview?.data?.explanation}</DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-1">
            <div><strong>Model:</strong> {costPreview?.data?.model}</div>
            <div><strong>Units:</strong> {costPreview?.data?.units}</div>
            <div className="flex items-center justify-between pt-2 border-t border-zinc-200 font-bold">
              <span>Estimated cost</span><span>{fmtCost(costPreview?.data?.cost_usd)}</span>
            </div>
            <p className="text-[11px] text-zinc-500">Billed to: {costPreview?.data?.billed_to}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setCostPreview(null)}
              data-testid="creative-cost-cancel">Cancel</Button>
            <Button className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
              onClick={() => costPreview?.onConfirm?.()} data-testid="creative-cost-confirm">
              <Sparkles className="h-3.5 w-3.5 mr-1" /> Confirm & spend {fmtCost(costPreview?.data?.cost_usd)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save-to-library dialog */}
      <Dialog open={!!saveTarget} onOpenChange={(o) => !o && setSaveTarget(null)}>
        <DialogContent className="rounded-sm max-w-md" data-testid="creative-save-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4 text-[#002FA7]" /> Save to Library
            </DialogTitle>
            <DialogDescription>
              The AI platform used and its licence conditions are recorded automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-zinc-500">Asset name</Label>
              <Input value={saveName} onChange={(e) => setSaveName(e.target.value)}
                className="rounded-sm mt-1" data-testid="save-asset-name" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Description</Label>
              <Textarea rows={2} value={saveDesc} onChange={(e) => setSaveDesc(e.target.value)}
                className="rounded-sm mt-1" data-testid="save-asset-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setSaveTarget(null)}>Cancel</Button>
            <Button className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={!saveName.trim()} onClick={saveToLibrary}
              data-testid="save-asset-confirm">
              <Check className="h-3.5 w-3.5 mr-1" /> Save asset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* URL asset dialog */}
      <Dialog open={urlDialog} onOpenChange={setUrlDialog}>
        <DialogContent className="rounded-sm max-w-md" data-testid="creative-url-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="h-4 w-4 text-[#002FA7]" /> Add a YouTube or Vimeo video
            </DialogTitle>
            <DialogDescription>
              Paste a public YouTube or Vimeo URL — we&apos;ll pull the title, thumbnail,
              author and duration automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-zinc-500">Video URL</Label>
              <Input value={urlValue} onChange={(e) => setUrlValue(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=…"
                className="rounded-sm mt-1 font-mono text-xs"
                data-testid="creative-url-input" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Display name (optional)</Label>
              <Input value={urlName} onChange={(e) => setUrlName(e.target.value)}
                className="rounded-sm mt-1"
                placeholder="Defaults to the video's own title"
                data-testid="creative-url-name" />
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Description (optional)</Label>
              <Textarea rows={2} value={urlDesc}
                onChange={(e) => setUrlDesc(e.target.value)}
                className="rounded-sm mt-1"
                data-testid="creative-url-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm"
              onClick={() => setUrlDialog(false)}>Cancel</Button>
            <Button className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
              disabled={urlBusy || urlValue.trim().length < 8}
              onClick={saveUrl}
              data-testid="creative-url-save">
              {urlBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
              Add to Library
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
